import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Phone, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Floating AI Sales Assistant bubble.
 * - Renders a small button bottom-right
 * - Opens a chat window that talks to /api/sales-chat/message (GPT-4o)
 * - Always shows a "Talk to a live person on WhatsApp" CTA
 */
export default function SalesChatBubble() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]); // {role, content}
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/sales-chat/settings`);
        const json = await res.json();
        setSettings(json);
        if (json.enabled && json.greeting && messages.length === 0) {
          setMessages([{ role: 'assistant', content: json.greeting }]);
        }
      } catch (e) { /* no chat available */ }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setSending(true);
    try {
      const res = await fetch(`${API}/sales-chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: text,
          history: messages,
        }),
      });
      const json = await res.json();
      if (json.session_id) setSessionId(json.session_id);
      setMessages([...next, { role: 'assistant', content: json.reply || "Sorry — couldn't reach the assistant." }]);
    } catch (e) {
      setMessages([...next, { role: 'assistant', content: 'Connection issue. Please try again or tap the WhatsApp button below.' }]);
    } finally { setSending(false); }
  };

  if (!settings?.enabled) return null;

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open chat"
          data-testid="sales-chat-launcher"
          className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-black shadow-[0_8px_28px_rgba(251,191,36,0.45)] flex items-center justify-center hover:scale-105 active:scale-95 transition-transform group"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-black animate-pulse" />
          <span className="sr-only">Chat with us</span>
        </button>
      )}

      {/* Window */}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-sm h-[78vh] sm:h-[560px] bg-zinc-950 border-2 border-amber-500/30 rounded-2xl shadow-[0_18px_60px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden" data-testid="sales-chat-window">
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-zinc-900 to-black border-b border-amber-500/20 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4 text-amber-300" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-white truncate">FORTHEWISEPRO Assistant</p>
                <p className="text-[10px] text-emerald-300 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Online · Powered by AI
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="sales-chat-close" className="text-gray-400 hover:text-white p-1">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-[#0a0a0a]">
            {messages.length === 0 && (
              <p className="text-xs text-gray-500 text-center py-8">Loading…</p>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${m.role}`}>
                <div className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-amber-500 text-black font-medium'
                    : 'bg-zinc-900 text-gray-100 border border-zinc-800'
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="rounded-2xl px-3.5 py-2 bg-zinc-900 border border-zinc-800 text-gray-400 text-sm flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> typing…
                </div>
              </div>
            )}
          </div>

          {/* WhatsApp CTA */}
          {settings.whatsapp_link && (
            <a
              href={settings.whatsapp_link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sales-chat-whatsapp"
              className="mx-3 mb-2 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-bold hover:bg-emerald-500/25 transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              Prefer a live person? Chat on WhatsApp ({settings.whatsapp_number})
            </a>
          )}

          {/* Input */}
          <div className="px-3 pb-3 pt-1 border-t border-zinc-800 bg-zinc-950">
            <div className="flex items-center gap-2">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Type your question…"
                disabled={sending}
                data-testid="sales-chat-input"
                className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-amber-500/40"
              />
              <Button
                size="sm"
                onClick={send}
                disabled={sending || !input.trim()}
                className="bg-amber-500 hover:bg-amber-400 text-black disabled:bg-zinc-800 disabled:text-gray-500"
                data-testid="sales-chat-send"
              >
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
            <p className="text-[9px] text-gray-600 mt-1.5 text-center">
              AI assistant · Education only · Not financial advice
            </p>
          </div>
        </div>
      )}
    </>
  );
}
